import { clsx } from 'clsx'
import { useEffect, useRef } from 'react'
import { NavLink, useLocation } from 'react-router'
import { Check } from './icons'

export type StepState = 'todo' | 'partial' | 'done' | 'review' | 'locked'

export type Step = {
  id: string
  number: string
  title: string
  href: string
  state: StepState
  /** Short line under the title, e.g. "3 to fill". */
  note?: string
}

const STATE_TEXT: Record<StepState, string> = {
  todo: 'Not started',
  partial: 'In progress',
  done: 'Complete',
  review: 'Needs review',
  locked: 'Locked',
}

/**
 * Vertical step list for the onboarding workspace. Collapses to a horizontal scroller below lg.
 * State is conveyed by an icon and visually-hidden text, never colour alone.
 */
export function StepRail({ steps, ariaLabel = 'Onboarding steps', className }: { steps: Step[]; ariaLabel?: string; className?: string }) {
  const list = useRef<HTMLOListElement>(null)
  const { pathname } = useLocation()
  const placed = useRef(false)
  // As a horizontal strip (below lg) only a few steps fit, so bring the current one into the middle of it.
  // Scrolls the strip itself, never the page.
  useEffect(() => {
    const ol = list.current
    const current = ol?.querySelector<HTMLElement>('[aria-current="page"]')?.parentElement
    if (!ol || !current || ol.scrollWidth <= ol.clientWidth) return
    // Arriving on the page it is simply in place; moving between steps it glides.
    const glide = placed.current && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    placed.current = true
    ol.scrollTo({ left: current.offsetLeft - (ol.clientWidth - current.offsetWidth) / 2, behavior: glide ? 'smooth' : 'auto' })
  }, [pathname])
  return (
    <nav aria-label={ariaLabel} className={clsx('lg:block', className)}>
      {/* `relative`: the sr-only state labels are absolutely positioned and must stay inside the strip, or they widen the page. */}
      <ol ref={list} className="relative flex gap-1 overflow-x-auto pb-2 lg:flex-col lg:gap-0 lg:overflow-visible lg:pb-0 [scrollbar-width:thin]">
        {steps.map((s) => (
          <li key={s.id} className="shrink-0">
            <NavLink
              to={s.href}
              aria-disabled={s.state === 'locked' || undefined}
              className={({ isActive }) =>
                clsx(
                  'group flex min-h-11 items-center gap-3 border-l-2 px-3 py-2 text-small transition-colors lg:px-4',
                  isActive ? 'border-ink bg-gray-50 text-ink' : 'border-transparent text-charcoal hover:bg-gray-50 hover:text-ink',
                  s.state === 'locked' && 'pointer-events-none text-dim',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className="w-6 shrink-0 font-display text-h5 tabular-nums text-dim group-hover:text-ink" aria-hidden="true">
                    {s.number}
                  </span>
                  <span className={clsx('flex-1 whitespace-nowrap lg:whitespace-normal', isActive && 'font-medium')}>
                    {s.title}
                    {s.note && <span className="mt-1 block w-fit border-l-2 border-warn bg-warn-wash px-1.5 py-px text-[0.75rem] font-medium text-warn tabular-nums">{s.note}</span>}
                  </span>
                  <StepMark state={s.state} />
                  {isActive && <span className="sr-only">(current)</span>}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ol>
    </nav>
  )
}

export function StepMark({ state }: { state: StepState }) {
  return (
    <span className="ml-2 inline-flex shrink-0 items-center">
      <span
        aria-hidden="true"
        className={clsx(
          'grid h-4 w-4 place-items-center border',
          state === 'done' && 'border-ok bg-ok text-paper',
          state === 'partial' && 'border-ink bg-[linear-gradient(90deg,var(--color-ink)_50%,transparent_50%)]',
          state === 'review' && 'border-warn bg-warn-wash',
          state === 'todo' && 'border-gray-400',
          state === 'locked' && 'border-gray-300 bg-gray-100',
        )}
      >
        {state === 'done' && <Check size={10} strokeWidth={3} />}
        {state === 'review' && <span className="h-1.5 w-1.5 bg-warn" />}
      </span>
      <span className="sr-only">{STATE_TEXT[state]}</span>
    </span>
  )
}
